/** Encrypted, immutable off-device snapshots of prepared Solana journal records. */
import { ethers } from "ethers";
import { deriveSpendingKeys } from "../../joinsplit/keys";
import { formatMpkHex, hashMpkHex } from "../../utils/format";
import { withDeadline } from "../../utils/withDeadline";
import {
  exportSolanaJournalEnvelope,
  importSolanaJournalEnvelope,
  solanaJournalIntentFingerprint,
  validateSolanaJournalEnvelope,
  type SolanaJournalEnvelope,
  type SolanaJournalRecord,
  type SolanaJournalScope,
} from "./journal";

export interface SolanaJournalBackup {
  id: string;
  network: string;
  programId: string;
  accountAddress: string;
  envelope: SolanaJournalEnvelope;
}

export interface SolanaJournalBackupOptions {
  baseUrl: string;
  headers: Record<string, string>;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

export interface SolanaJournalBackupVariant {
  backupId: string;
  record: SolanaJournalRecord;
  envelope: SolanaJournalEnvelope;
}

export interface SolanaJournalRestoreResult {
  restored: number;
  quarantined: number;
  /** All authenticated versions remain available for chain-matched reconciliation. */
  variants: Map<string, SolanaJournalBackupVariant[]>;
}

function canonicalBackup(backup: SolanaJournalBackup): SolanaJournalBackup {
  return {
    id: backup.id,
    network: backup.network,
    programId: backup.programId,
    accountAddress: backup.accountAddress,
    envelope: {
      schemaVersion: backup.envelope.schemaVersion,
      iv: backup.envelope.iv,
      ciphertext: backup.envelope.ciphertext,
    },
  };
}

export function solanaJournalBackupDigest(backup: SolanaJournalBackup): string {
  return ethers
    .sha256(ethers.toUtf8Bytes(JSON.stringify(canonicalBackup(backup))))
    .slice(2);
}

function envelopeHash(envelope: SolanaJournalEnvelope): string {
  return ethers
    .sha256(ethers.toUtf8Bytes(JSON.stringify(envelope)))
    .slice(2, 26);
}

function backupId(
  journalId: string,
  createdAt: number,
  envelope: SolanaJournalEnvelope,
): string {
  if (!/^[0-9a-f]{64}$/.test(journalId) || !Number.isSafeInteger(createdAt)) {
    throw new Error("Solana recovery journal identity is malformed");
  }
  return `${journalId}.${createdAt.toString(36)}.${envelopeHash(envelope)}`;
}

function parseBackupId(id: string): {
  journalId: string;
  createdAt: number;
  hash: string;
} {
  const match = /^([0-9a-f]{64})\.([0-9a-z]{1,12})\.([0-9a-f]{24})$/.exec(id);
  if (!match) throw new Error("Solana recovery backup id is malformed");
  const createdAt = parseInt(match[2], 36);
  if (!Number.isSafeInteger(createdAt) || createdAt < 0) {
    throw new Error("Solana recovery backup timestamp is malformed");
  }
  return { journalId: match[1], createdAt, hash: match[3] };
}

function endpoint(baseUrl: string, recipientKey: string): string {
  const path = `/api/v1/recovery/journal/${recipientKey}`;
  return /[?&]path=$/.test(baseUrl)
    ? `${baseUrl}${path.slice(1)}`
    : `${baseUrl.replace(/\/+$/, "")}${path}`;
}

async function context(
  scope: SolanaJournalScope,
  options: SolanaJournalBackupOptions,
) {
  if (!options.headers.Authorization?.startsWith("Bearer ")) {
    throw new Error("Sign in before backing up or restoring Solana notes");
  }
  const keys = await deriveSpendingKeys(scope.spendingKey);
  const recipientKey = await hashMpkHex(formatMpkHex(keys.mpk));
  return {
    url: endpoint(options.baseUrl, recipientKey),
    headers: { "Content-Type": "application/json", ...options.headers },
    fetchImpl: options.fetchImpl ?? fetch,
  };
}

export async function exportSolanaJournalBackup(
  scope: SolanaJournalScope,
  journalId: string,
  expectedRecord: SolanaJournalRecord,
): Promise<SolanaJournalBackup> {
  const { envelope, record } = await exportSolanaJournalEnvelope(
    scope,
    journalId,
  );
  if (
    solanaJournalIntentFingerprint(record) !==
    solanaJournalIntentFingerprint(expectedRecord)
  ) {
    throw new Error(
      "Solana journal version changed in another tab before recovery backup",
    );
  }
  return canonicalBackup({
    id: backupId(journalId, record.createdAt, envelope),
    network: scope.network,
    programId: scope.programId,
    accountAddress: scope.account,
    envelope,
  });
}

/** The caller must receive a matching durable ack before exposing the transaction. */
export async function persistSolanaJournalBackup(
  scope: SolanaJournalScope,
  journalId: string,
  expectedRecord: SolanaJournalRecord,
  options: SolanaJournalBackupOptions,
): Promise<void> {
  const backup = await exportSolanaJournalBackup(
    scope,
    journalId,
    expectedRecord,
  );
  const { url, headers, fetchImpl } = await context(scope, options);
  const result = await withDeadline(
    async (signal) => {
      const response = await fetchImpl(url, {
        method: "PUT",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers,
        body: JSON.stringify(backup),
        signal,
      });
      if (!response.ok) {
        throw new Error(
          `Encrypted Solana recovery backup rejected (${response.status}); transaction was not submitted`,
        );
      }
      return response.json();
    },
    "Encrypted Solana recovery backup timed out; transaction was not submitted",
    options.signal,
    30_000,
  );
  if (
    result?.success !== true ||
    result.id !== backup.id ||
    result.digest !== solanaJournalBackupDigest(backup)
  ) {
    throw new Error(
      "Solana recovery backup acknowledgement does not match the encrypted record",
    );
  }
}

/** Restore ciphertext only. Chain reconciliation still decides whether it landed. */
export async function restoreSolanaJournalBackups(
  scope: SolanaJournalScope,
  options: SolanaJournalBackupOptions,
): Promise<SolanaJournalRestoreResult> {
  const { url, headers, fetchImpl } = await context(scope, options);
  const latest = new Map<
    string,
    { createdAt: number; submitted: boolean; backup: SolanaJournalBackup }
  >();
  const variants = new Map<string, SolanaJournalBackupVariant[]>();
  let quarantined = 0;
  let cursor: string | undefined;
  const cursors = new Set<string>();
  do {
    if (cursor) {
      if (cursors.has(cursor) || cursors.size >= 100) {
        throw new Error(
          "Solana recovery backup pagination repeated or exceeded its limit",
        );
      }
      cursors.add(cursor);
    }
    const pageUrl = cursor
      ? `${url}${url.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(cursor)}`
      : url;
    const result = await withDeadline(
      async (signal) => {
        const response = await fetchImpl(pageUrl, {
          method: "GET",
          credentials: "same-origin",
          cache: "no-store",
          redirect: "error",
          headers,
          signal,
        });
        if (!response.ok) {
          throw new Error(
            `Encrypted Solana recovery restore unavailable (${response.status})`,
          );
        }
        return response.json();
      },
      "Encrypted Solana recovery restore timed out; retry recovery",
      options.signal,
      30_000,
    );
    if (
      result?.success !== true ||
      !Array.isArray(result.records) ||
      result.records.length > 100 ||
      (result.nextCursor != null && typeof result.nextCursor !== "string")
    ) {
      throw new Error("Malformed encrypted Solana recovery response");
    }
    for (const item of result.records) {
      // A session token can write opaque ciphertext but cannot forge the
      // privacy-key AES-GCM tag. Quarantine one poison row without hiding the
      // other owner's valid immutable backups on this page.
      try {
        if (!item?.record || item.id !== item.record.id) {
          throw new Error("identity mismatch");
        }
        const backup = canonicalBackup(item.record as SolanaJournalBackup);
        if (
          backup.network !== scope.network ||
          backup.programId !== scope.programId ||
          backup.accountAddress !== scope.account
        ) {
          throw new Error("namespace mismatch");
        }
        if (item.digest !== solanaJournalBackupDigest(backup)) {
          throw new Error("digest mismatch");
        }
        const parsed = parseBackupId(backup.id);
        if (parsed.hash !== envelopeHash(backup.envelope)) {
          throw new Error("envelope id mismatch");
        }
        const decrypted = await validateSolanaJournalEnvelope(
          scope,
          parsed.journalId,
          backup.envelope,
        );
        if (decrypted.createdAt !== parsed.createdAt) {
          throw new Error("version mismatch");
        }
        const group = variants.get(parsed.journalId) ?? [];
        group.push({
          backupId: backup.id,
          record: decrypted,
          envelope: backup.envelope,
        });
        variants.set(parsed.journalId, group);
        const prior = latest.get(parsed.journalId);
        const submitted = Boolean(decrypted.signature);
        if (
          !prior ||
          decrypted.createdAt > prior.createdAt ||
          (decrypted.createdAt === prior.createdAt &&
            submitted &&
            !prior.submitted)
        ) {
          latest.set(parsed.journalId, {
            createdAt: decrypted.createdAt,
            submitted,
            backup,
          });
        }
      } catch (error) {
        if (error instanceof Error && error.message === "namespace mismatch") {
          throw new Error(
            "Solana recovery backup owner or network scope mismatch",
          );
        }
        quarantined += 1;
      }
    }
    cursor = result.nextCursor || undefined;
  } while (cursor);

  let restored = 0;
  for (const [journalId, item] of latest) {
    if (
      await importSolanaJournalEnvelope(scope, journalId, item.backup.envelope)
    ) {
      restored += 1;
    }
  }
  return { restored, quarantined, variants };
}
